import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import apiService from '../services/apiService';
import { 
  Users, 
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  UserCheck,
  TrendingUp,
  Building2,
  Eye,
  Download,
  Plus,
  X,
  AlertTriangle,
  RefreshCw,
  LogOut
} from 'lucide-react';

// Fonction pour obtenir le nom du mois
const getMonthName = (monthNumber) => {
  const months = [
    '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  return months[monthNumber] || `Mois ${monthNumber}`;
};

// Fonction pour formater les montants avec suffixes K et M
const formatAmount = (amount) => {
  // Convertir en nombre si c'est une chaîne
  let numAmount = parseFloat(amount) || 0;
  
  // Si c'est zéro, retourner "0"
  if (numAmount === 0) return '0';
  
  const absAmount = Math.abs(numAmount);
  
  if (absAmount >= 1000000) {
    const millions = numAmount / 1000000;
    // Arrondir à 1 décimale maximum et supprimer les .0 inutiles
    return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  } else if (absAmount >= 1000) {
    const thousands = numAmount / 1000;
    // Arrondir à 1 décimale maximum et supprimer les .0 inutiles
    return `${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)}K`;
  }
  
  // Pour les nombres < 1000, utiliser le format entier
  return Math.round(numAmount).toString();
};

const FraternityMembersPage = () => {
  const { user, isAuthenticated, logout, getModerateurRedirectUrl, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const { fraternityName } = useParams();
  
  // États de base
  const [fraternityData, setFraternityData] = useState(null);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // États pour les cotisations
  const [showAddContributionModal, setShowAddContributionModal] = useState(false);
  const [selectedMemberForContribution, setSelectedMemberForContribution] = useState(null);
  const [contributionFormData, setContributionFormData] = useState({
    amount: '',
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    status: 'pending',
    payment_method: 'cash',
    paid_at: '',
    notes: ''
  });
  const [contributionLoading, setContributionLoading] = useState(false);
  const [contributionError, setContributionError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!user?.zone) {
      navigate('/dashboard');
      return;
    }

    // === CONTRÔLE ACCÈS MODÉRATEUR ===
    // Si c'est un modérateur, rediriger vers sa fraternité
    const moderateurRedirectUrl = getModerateurRedirectUrl();
    if (moderateurRedirectUrl) {
      console.log('🔐 Redirection modérateur vers:', moderateurRedirectUrl);
      
      // Si pas déjà sur sa page de fraternité, rediriger
      if (!fraternityName || decodeURIComponent(fraternityName).toLowerCase() !== user.fraternity.toLowerCase()) {
        navigate(moderateurRedirectUrl, { replace: true });
        return;
      }
    }

    // Charger les données si fraternité spécifiée
    if (fraternityName) {
      loadFraternityMembers();
    }
  }, [isAuthenticated, user, navigate, fraternityName, getModerateurRedirectUrl]);

  const loadFraternityMembers = async () => {
    try {
      setIsLoading(true);
      console.log('Chargement des membres de la fraternité:', fraternityName);

      // Vérification de sécurité supplémentaire pour les modérateurs
      const moderateurRedirectUrl = getModerateurRedirectUrl();
      if (moderateurRedirectUrl) {
        const decodedFraternityName = decodeURIComponent(fraternityName);
        if (decodedFraternityName.toLowerCase() !== user.fraternity.toLowerCase()) {
          console.log('🚫 Accès refusé: le modérateur n\'a pas accès à cette fraternité');
          navigate(moderateurRedirectUrl, { replace: true });
          return;
        }
      }

      // Utiliser l'endpoint du backend pour récupérer les membres de la fraternité
      const response = await apiService.get(`/zone-dashboard/fraternity/${encodeURIComponent(fraternityName)}/members`);
      console.log('Réponse fraternité:', response);

      setFraternityData(response);
      setMembers(response.members || []);
      
    } catch (error) {
      console.error('Erreur lors du chargement des membres de la fraternité:', error);
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (member) => {
    setSelectedMember(member);
    setShowDetailsModal(true);
  };

  const handleExportMembers = () => {
    try {
      console.log('Export des membres de la fraternité:', fraternityName, members.length);
      
      if (members.length === 0) {
        alert('Aucun membre à exporter');
        return;
      }
      
      // Préparer les données pour l'export
      const exportData = members.map(member => ({
        'Prénom': member.first_name || '',
        'Nom': member.last_name || '',
        'Email': member.email || '',
        'Téléphone': member.phone || '',
        'Statut': member.status || 'actif',
        'Zone': member.zone || '',
        'Fraternité': fraternityName || '',
        'Cotisation mensuelle (F CFA)': member.contribution_monthly || 0,
        'Cotisation annuelle attendue (F CFA)': member.expected_amount || 0,
        'Montant payé 2024 (F CFA)': member.total_paid || 0,
        'Pourcentage paiement': `${member.payment_percentage || 0}%`,
        'Date d\'adhésion': member.join_date ? new Date(member.join_date).toLocaleDateString('fr-FR') : '',
        'Date de création': member.created_at ? new Date(member.created_at).toLocaleDateString('fr-FR') : '',
      }));

      // Créer le contenu CSV
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => headers.map(header => {
          const value = row[header];
          // Échapper les guillemets et entourer de guillemets si nécessaire
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');

      // Créer le fichier et déclencher le téléchargement
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Nom du fichier avec date/heure et nom de la fraternité
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 19).replace(/[T:]/g, '-');
      const fraternityNameSafe = fraternityName.replace(/[^a-zA-Z0-9]/g, '-');
      link.setAttribute('download', `membres-fraternite-${fraternityNameSafe}-${dateStr}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('✅ Export réussi - Fichier téléchargé');
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export des membres');
    }
  };

  // === GESTION DÉCONNEXION ===
  const handleLogout = async () => {
    console.log('🔓 Déconnexion du modérateur');
    await logout();
    navigate('/login', { replace: true });
  };

  // === GESTION DES COTISATIONS ===

  const handleOpenAddContribution = (member) => {
    setSelectedMemberForContribution(member);
    setContributionFormData({
      amount: member.contribution_monthly || '',
      period_month: new Date().getMonth() + 1,
      period_year: new Date().getFullYear(),
      status: 'pending',
      payment_method: 'cash',
      paid_at: '',
      notes: ''
    });
    setContributionError('');
    setShowAddContributionModal(true);
  };

  const resetContributionForm = () => {
    setSelectedMemberForContribution(null);
    setContributionFormData({
      amount: '',
      period_month: new Date().getMonth() + 1,
      period_year: new Date().getFullYear(),
      status: 'pending',
      payment_method: 'cash',
      paid_at: '',
      notes: ''
    });
    setContributionError('');
  };

  const handleSubmitContribution = async (e) => {
    e.preventDefault();
    setContributionError('');
    
    if (!selectedMemberForContribution) {
      setContributionError('Veuillez sélectionner un membre.');
      return;
    }

    if (!contributionFormData.amount || contributionFormData.amount <= 0) {
      setContributionError('Le montant doit être supérieur à 0.');
      return;
    }

    try {
      setContributionLoading(true);
      
      const payload = {
        user_id: selectedMemberForContribution.id,
        amount: parseFloat(contributionFormData.amount),
        period_month: parseInt(contributionFormData.period_month),
        period_year: parseInt(contributionFormData.period_year),
        status: contributionFormData.status,
        payment_method: contributionFormData.payment_method,
        paid_at: contributionFormData.status === 'paid' && contributionFormData.paid_at 
          ? contributionFormData.paid_at 
          : null,
        notes: contributionFormData.notes || ''
      };

      const response = await apiService.post('/zone-dashboard/contributions', payload);
      
      setShowAddContributionModal(false);
      resetContributionForm();
      
      // Rafraîchir les données
      await loadFraternityMembers();
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la cotisation:', error);
      setContributionError(
        error.message || 'Erreur lors de l\'ajout de la cotisation. Veuillez réessayer.'
      );
    } finally {
      setContributionLoading(false);
    }
  };

  const calculateStats = () => {
    if (members.length === 0) return { avgPayment: 0, totalExpected: 0, totalPaid: 0 };

    console.log('=== DEBUG CALCUL STATS ===');
    console.log('Nombre de membres:', members.length);
    
    // Debug détaillé pour chaque membre
    members.forEach((member, index) => {
      console.log(`Membre ${index + 1}:`, {
        name: `${member.first_name} ${member.last_name}`,
        expected_amount: member.expected_amount,
        total_paid: member.total_paid,
        payment_percentage: member.payment_percentage,
        contribution_monthly: member.contribution_monthly
      });
    });

    // Calcul des totaux
    const totalExpected = members.reduce((sum, member) => {
      const amount = parseFloat(member.expected_amount) || 0;
      console.log(`Expected amount pour ${member.first_name}: ${member.expected_amount} -> ${amount}`);
      return sum + amount;
    }, 0);
    
    const totalPaid = members.reduce((sum, member) => {
      const amount = parseFloat(member.total_paid) || 0;
      console.log(`Total paid pour ${member.first_name}: ${member.total_paid} -> ${amount}`);
      return sum + amount;
    }, 0);
    
    // Calcul du taux de paiement moyen - moyenne des pourcentages individuels
    const avgPayment = members.length > 0 ? 
      members.reduce((sum, member) => {
        const memberPercentage = parseFloat(member.payment_percentage) || 0;
        console.log(`Payment percentage pour ${member.first_name}: ${member.payment_percentage} -> ${memberPercentage}%`);
        return sum + memberPercentage;
      }, 0) / members.length 
      : 0;
    
    
    console.log('=== RÉSULTATS CALCUL ===');
    console.log('Total Expected:', totalExpected);
    console.log('Total Paid:', totalPaid);
    console.log('Nombre de membres:', members.length);
    console.log('Taux moyen (moyenne des %):', avgPayment);
    console.log('Taux global (si calculé autrement):', totalExpected > 0 ? (totalPaid / totalExpected) * 100 : 0);
    console.log('===========================');

    return { 
      avgPayment: Math.round(avgPayment), 
      totalExpected: Math.round(totalExpected), 
      totalPaid: Math.round(totalPaid) 
    };
  };

  const stats = calculateStats();
  
  // Debug temporaire
  console.log('Debug Stats:', {
    members: members.length,
    totalExpected: stats.totalExpected,
    totalPaid: stats.totalPaid,
    avgPayment: stats.avgPayment,
    sampleMember: members[0]
  });

  if (!isAuthenticated || !user?.zone) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link
                to="/zone-dashboard"
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Retour au Dashboard
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Fraternité {decodeURIComponent(fraternityName || '')}
                </h1>
                <p className="text-sm text-gray-600">
                  Zone {fraternityData?.zone} - {members.length} membre{members.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {user.first_name} {user.last_name}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                {user.role?.toUpperCase()}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 ml-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                title="Se déconnecter"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistiques de la fraternité */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Membres</p>
                <p className="text-2xl font-bold text-gray-900">{members.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <UserCheck className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Membres Actifs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.filter(m => m.status === 'actif').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-yellow-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Cotisations Payées</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatAmount(stats.totalPaid)} FCFA
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Taux de Paiement</p>
                <p className="text-2xl font-bold text-gray-900">{stats.avgPayment}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Résumé financier */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              Résumé Financier de la Fraternité
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-green-600">Cotisations Collectées</p>
                <p className="text-2xl font-bold text-green-900">{formatAmount(stats.totalPaid)} FCFA</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-blue-600">Cotisations Attendues</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatAmount(stats.totalExpected)} FCFA</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-yellow-600">Reste à Collecter</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {formatAmount(stats.totalExpected - stats.totalPaid)} FCFA
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau des membres */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Membres de la Fraternité ({members.length})
              </h2>
              <div className="flex space-x-2">
                {user?.role?.toLowerCase() !== 'moderateur' && (

                <Link
                  to="/zone-members"
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Tous les Membres
                </Link>
                )}
                <button 
                  onClick={handleExportMembers}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  title="Exporter les membres au format CSV"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exporter
                </button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Chargement des membres...</p>
            </div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun membre dans cette fraternité</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Membre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cotisation Mensuelle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paiements
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pourcentage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {member.first_name} {member.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            Depuis {member.join_date ? new Date(member.join_date).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <Mail className="h-4 w-4 mr-2 text-gray-400" />
                            {member.email}
                          </div>
                          {member.phone && (
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-2 text-gray-400" />
                              {member.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          member.status === 'actif' 
                            ? 'bg-green-100 text-green-800'
                            : member.status === 'inactif'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {member.status || 'actif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-1 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {formatAmount(member.contribution_monthly || 0)} F CFA
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            {formatAmount(member.total_paid || 0)} F CFA
                          </div>
                          <div className="text-xs text-gray-500">
                            sur {formatAmount(member.expected_amount || 0)} F
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className={`h-2 rounded-full ${
                                (member.payment_percentage || 0) >= 80 ? 'bg-green-500' :
                                (member.payment_percentage || 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(member.payment_percentage || 0, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {member.payment_percentage || 0}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(member)}
                          className="text-blue-600 hover:text-blue-900 transition-colors mr-3"
                          title="Voir détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {!isReadOnly && (
                        <button
                          onClick={() => handleOpenAddContribution(member)}
                          className="text-green-600 hover:text-green-900 transition-colors"
                          title="Ajouter une cotisation"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal détails membre */}
      {showDetailsModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 text-justify">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Détails - {selectedMember.first_name} {selectedMember.last_name}
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Informations personnelles</h4>
                  <div className="space-y-2">
                    <p><span className="font-medium">Nom:</span> {selectedMember.first_name} {selectedMember.last_name}</p>
                    <p><span className="font-medium">Email:</span> {selectedMember.email}</p>
                    <p><span className="font-medium">Téléphone:</span> {selectedMember.phone || 'Non renseigné'}</p>
                    <p><span className="font-medium">Statut:</span> {selectedMember.status || 'actif'}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Performance financière</h4>
                  <div className="space-y-2">
                    <p><span className="font-medium">Cotisation mensuelle:</span> {(selectedMember.contribution_monthly || 0).toLocaleString()} F CFA</p>
                    <p><span className="font-medium">Attendu:</span> {(selectedMember.expected_amount || 0).toLocaleString()} F CFA</p>
                    <p><span className="font-medium">Payé:</span> {(selectedMember.total_paid || 0).toLocaleString()} F CFA</p>
                    <p><span className="font-medium">Reste à payer:</span> {((selectedMember.expected_amount || 0) - (selectedMember.total_paid || 0)).toLocaleString()} F CFA</p>
                    <p><span className="font-medium">Pourcentage de paiement:</span> 
                      <span className={`ml-1 px-2 py-1 text-xs font-semibold rounded ${
                        (selectedMember.payment_percentage || 0) >= 80 ? 'bg-green-100 text-green-800' :
                        (selectedMember.payment_percentage || 0) >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedMember.payment_percentage || 0}%
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Section des informations de cotisations récentes */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Contribution</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-blue-600">Période:</span>
                      <p className="text-gray-700">
                        {selectedMember.period_month && selectedMember.period_year 
                          ? `${getMonthName(parseInt(selectedMember.period_month))} ${selectedMember.period_year}`
                          : selectedMember.period_year 
                            ? `Année ${selectedMember.period_year}`
                            : 'Aucune contribution enregistrée'
                        }
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-600">Méthode de paiement:</span>
                      <p className="text-gray-700 capitalize">
                        {selectedMember.last_payment_method || 'N/A'}
                      </p>
                    </div>
                    {selectedMember.last_payment_date && (
                      <div className="col-span-2">
                        <span className="font-medium text-blue-600">Date du dernier paiement:</span>
                        <p className="text-gray-700">
                          {new Date(selectedMember.last_payment_date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Progression des paiements</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Progression annuelle</span>
                    <span className="text-sm font-medium">{selectedMember.payment_percentage || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        (selectedMember.payment_percentage || 0) >= 80 ? 'bg-green-500' :
                        (selectedMember.payment_percentage || 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(selectedMember.payment_percentage || 0, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>0 F</span>
                    <span>{(selectedMember.expected_amount || 0).toLocaleString()} F CFA</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout de cotisation */}
      {showAddContributionModal && selectedMemberForContribution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  💳 Ajouter une cotisation
                </h3>
                <button
                  onClick={() => {
                    setShowAddContributionModal(false);
                    resetContributionForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitContribution} className="p-6 space-y-6">
              {/* Affichage des erreurs */}
              {contributionError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{contributionError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Informations du membre */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                  👤 Membre sélectionné
                </h4>
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white font-bold">
                    {selectedMemberForContribution.first_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedMemberForContribution.first_name} {selectedMemberForContribution.last_name}
                    </p>
                    <p className="text-xs text-gray-600">
                      {selectedMemberForContribution.email}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      ⛪ {decodeURIComponent(fraternityName || '')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Montant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant de la cotisation (FCFA) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={contributionFormData.amount}
                  onChange={(e) => setContributionFormData({
                    ...contributionFormData,
                    amount: e.target.value
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ex: 50000"
                  required
                />
              </div>

              {/* Période */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mois *
                  </label>
                  <select
                    value={contributionFormData.period_month}
                    onChange={(e) => setContributionFormData({
                      ...contributionFormData,
                      period_month: parseInt(e.target.value)
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {[
                      { value: 1, label: 'Janvier' },
                      { value: 2, label: 'Février' },
                      { value: 3, label: 'Mars' },
                      { value: 4, label: 'Avril' },
                      { value: 5, label: 'Mai' },
                      { value: 6, label: 'Juin' },
                      { value: 7, label: 'Juillet' },
                      { value: 8, label: 'Août' },
                      { value: 9, label: 'Septembre' },
                      { value: 10, label: 'Octobre' },
                      { value: 11, label: 'Novembre' },
                      { value: 12, label: 'Décembre' }
                    ].map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Année *
                  </label>
                  <select
                    value={contributionFormData.period_year}
                    onChange={(e) => setContributionFormData({
                      ...contributionFormData,
                      period_year: parseInt(e.target.value)
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {[2024, 2025, 2026, 2027].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Méthode de paiement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Méthode de paiement *
                </label>
                <select
                  value={contributionFormData.payment_method}
                  onChange={(e) => setContributionFormData({
                    ...contributionFormData,
                    payment_method: e.target.value
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="cash">💵 Espèces</option>
                  <option value="mobile_money">📱 Mobile Money</option>
                  <option value="bank_transfer">🏦 Virement Bancaire</option>
                  <option value="online">💳 Paiement en Ligne</option>
                </select>
              </div>

              {/* Statut */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut *
                </label>
                <select
                  value={contributionFormData.status}
                  onChange={(e) => setContributionFormData({
                    ...contributionFormData,
                    status: e.target.value
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="paid">✅ Payé</option>
                  <option value="pending">⏳ En attente</option>
                  <option value="overdue">⚠️ En retard</option>
                </select>
              </div>

              {/* Date de paiement (visible si statut = paid) */}
              {contributionFormData.status === 'paid' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de paiement
                  </label>
                  <input
                    type="date"
                    value={contributionFormData.paid_at}
                    onChange={(e) => setContributionFormData({
                      ...contributionFormData,
                      paid_at: e.target.value
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={contributionFormData.notes}
                  onChange={(e) => setContributionFormData({
                    ...contributionFormData,
                    notes: e.target.value
                  })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Commentaires supplémentaires (optionnel)..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddContributionModal(false);
                    resetContributionForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={contributionLoading}
                  className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 disabled:opacity-50 flex items-center"
                >
                  {contributionLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter la cotisation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FraternityMembersPage;